import React, { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CardWrapperDetailsPage } from '../../../../common/CardWrapper';
import { ANALYTICS_CONSTANTS } from '../../../../common/CareTeamCard/CareTeamUtils';
import { CareTeamHeader } from '../../../../common/CareTeamHeader';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../../common/Utils';
import { useAdobePageTrackEvent } from '../../../../hooks/adobeHook/useAdobePageTrackEvent';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useLagoon } from '../../../../hooks/useLagoon';
import { usePrimaryCare } from '../../../../hooks/usePrimaryCare';
import { useCareTeamStore } from '../../../../store/useCareTeamStore';
import { CareTeamState } from '../../../../store/useCareTeamStore/careTeamStore';
import { filterPcpConfiguration } from '../../../../utils/pcpConfigUtil';
import {
  isCnsRestrictedEditPcp,
  shouldShowProviderLimitBanner,
} from '../../../../utils/primaryCare.utils';
import {
  getLoggedInMember,
  getPolicyNumber,
  getReportingCode,
  isGroupMNRMember,
} from '../../../../utils/user.utils';
import { CareTeamDetailPage } from './CareTeamDetailPage';

export const CareTeamView = () => {
  const [pcpProvider, setPcpProvider] = useState(null);
  const [futurePcpProvider, setFuturePcpProvider] = useState(null);
  const [pcpChipValue, setPCPChipValue] = useState();
  const [pageNumber, setPageNumber] = useState(1);
  const featureFlags = useLagoon('feature-flags')();
  const enableCareTeamView = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CARE_TEAM_VIEW
  );
  const primaryCareData = usePrimaryCare();
  const customMesssges = useLagoon('custom-messages')();
  const coverageType = useCoverageType();
  const pcpLagoonConfig = useLagoon('pcp-configuration')();
  const allConfigs = useLagoon('config')();
  const maximumProviderLimit = allConfigs?.find(
    (row) => row.key === 'MAXIMUM_PROVIDER_LIMIT_FOR_CARETEAM'
  )?.value;

  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    pageName: ANALYTICS_CONSTANTS.CARE_TEAM,
  });
  const isPreEffective = useCurrentMemberIsPreEffective();

  const {
    careTeamProviderCount,
    setShowMaximumProviderLimitBanner,
    bookmarkedProviders,
  } = useCareTeamStore(
    useShallow((state: CareTeamState) => ({
      careTeamProviderCount: state.careTeamProviderCount,
      setShowMaximumProviderLimitBanner:
        state.setShowMaximumProviderLimitBanner,
      bookmarkedProviders: state.bookmarkedProviders,
    }))
  );
  const loggedInMemberInfo = getLoggedInMember();

  const hidePCPCard =
    !loggedInMemberInfo?.viewPcp || isGroupMNRMember(loggedInMemberInfo);

  const ENABLE_NEW_PCP_CARE_TEAM_FLOW = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_PCP_CARE_TEAM_FLOW
  );

  const showMaximumProviderLimitBanner = shouldShowProviderLimitBanner(
    ENABLE_NEW_PCP_CARE_TEAM_FLOW,
    bookmarkedProviders.length,
    careTeamProviderCount,
    maximumProviderLimit,
    hidePCPCard
  );

  const { primaryCare } = primaryCareData?.data;
  const memberPolicyNumber = getPolicyNumber(loggedInMemberInfo, coverageType);
  const memberReportingCode = getReportingCode(
    loggedInMemberInfo,
    coverageType
  );
  const isCnsReportingCode = isCnsRestrictedEditPcp(
    customMesssges,
    memberReportingCode,
    memberPolicyNumber
  );

  const filteredPcpConfig = React.useMemo(() => {
    const pcpConfigResult = filterPcpConfiguration(
      loggedInMemberInfo,
      pcpLagoonConfig,
      primaryCare || [],
      isCnsReportingCode,
      ENABLE_NEW_PCP_CARE_TEAM_FLOW,
      isPreEffective
    );
    return pcpConfigResult;
  }, [
    ENABLE_NEW_PCP_CARE_TEAM_FLOW,
    loggedInMemberInfo,
    pcpLagoonConfig,
    primaryCare,
    isCnsReportingCode,
    isPreEffective,
  ]);

  useEffect(() => {
    setShowMaximumProviderLimitBanner(showMaximumProviderLimitBanner);
  }, [showMaximumProviderLimitBanner, setShowMaximumProviderLimitBanner]);

  useEffect(() => {
    enableCareTeamView && adobePageTrackEvent();
  }, [adobePageTrackEvent, enableCareTeamView]);

  if (!enableCareTeamView) {
    return null;
  }

  return (
    <React.Fragment>
      <CareTeamHeader
        ENABLE_NEW_PCP_CARE_TEAM_FLOW={ENABLE_NEW_PCP_CARE_TEAM_FLOW}
        chipValue={pcpChipValue}
        filteredPcpConfig={filteredPcpConfig}
        futurePcpProvider={futurePcpProvider}
        pageNumber={pageNumber}
        pcpProvider={pcpProvider}
      />
      <CardWrapperDetailsPage>
        <CareTeamDetailPage
          ENABLE_NEW_PCP_CARE_TEAM_FLOW={ENABLE_NEW_PCP_CARE_TEAM_FLOW}
          filteredPcpConfig={filteredPcpConfig}
          setFuturePcpProvider={setFuturePcpProvider}
          setPCPChipValue={setPCPChipValue}
          setParentPageNumber={setPageNumber}
          setPcpProvider={setPcpProvider}
        />
      </CardWrapperDetailsPage>
    </React.Fragment>
  );
};
